/**
 * Reveal-then-claim gate for shoppers. A shopper who holds an unclaimed reveal — items revealed
 * by opening a box, or winnings from a completed battle — cannot open, create or join anything
 * until they claim it. The API refuses those actions with `reveal_pending`, naming the blocker.
 * This context turns that refusal into a modal: the page calls `promptClaim(hint)` from its error
 * handler, and <RevealClaimProvider> shows the claim popup.
 */
import type {
  BattleClaimResponse,
  BattleSellBackResponse,
  ClaimResponse,
  SellBackResponse,
} from "@loot-os/api-contract";
import { isLootApiErrorLike } from "@loot-os/react";
import { useMutation } from "@tanstack/react-query";
import { createContext, useContext } from "react";

import { invalidateAfterBattleClaim, invalidateAfterClaim, lootFetch, qk } from "./queries.js";
import { queryClient } from "./queryClient.js";

/** What is blocking the shopper, as named by a `reveal_pending` error's details. */
export type PendingRevealHint =
  | { kind: "open_group"; openGroupId: string }
  | { kind: "battle"; battleId: string };

/** Reads a `reveal_pending` error into the blocker it names, or null for any other error. */
export function pendingRevealFromError(err: unknown): PendingRevealHint | null {
  if (!isLootApiErrorLike(err) || err.code !== "reveal_pending") return null;
  const battleId = err.details?.battleId;
  if (typeof battleId === "string") return { kind: "battle", battleId };
  const openGroupId = err.details?.openGroupId;
  if (typeof openGroupId === "string") return { kind: "open_group", openGroupId };
  // A pending reveal we can't place: let the modal fall back to fetching the box reveal.
  return { kind: "open_group", openGroupId: "" };
}

export interface RevealClaimContextValue {
  /** Open the claim modal for the blocker a `reveal_pending` error named (or the box reveal). */
  promptClaim: (hint?: PendingRevealHint | null) => void;
}

export const RevealClaimContext = createContext<RevealClaimContextValue | null>(null);

/** Opens the "claim your unclaimed items" modal; throws outside <RevealClaimProvider>. */
export function useRevealClaim(): RevealClaimContextValue {
  const value = useContext(RevealClaimContext);
  if (!value) throw new Error("useRevealClaim must be used inside <RevealClaimProvider>");
  return value;
}

/** Claims one unclaimed box reveal (the whole open group); the balance moves here, not at open. */
export function useClaimBoxReveal() {
  return useMutation({
    mutationFn: (openGroupId: string) =>
      lootFetch<{ claim: ClaimResponse; replayed: boolean }>(`/reveals/${openGroupId}/claim`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      invalidateAfterClaim();
      void queryClient.invalidateQueries({ queryKey: qk.unclaimedReveal });
    },
  });
}

/**
 * Claims one unclaimed box reveal and sells every item straight back for its value, in one call:
 * the balance moves by Σ value − price (`claim_debit`, then `sell_credit`).
 */
export function useSellBackBoxReveal() {
  return useMutation({
    mutationFn: (openGroupId: string) =>
      lootFetch<{ sale: SellBackResponse; replayed: boolean }>(
        `/reveals/${openGroupId}/sell-back`,
        { method: "POST", body: JSON.stringify({}) },
      ),
    onSuccess: () => {
      invalidateAfterClaim();
      void queryClient.invalidateQueries({ queryKey: qk.unclaimedReveal });
    },
  });
}

/**
 * Claims a completed battle's winnings for the viewer's seat: a winner mints the pot, a loser pays
 * the entry and mints the battle's loser prize, when it carries one.
 */
export function useClaimBattleWinnings() {
  return useMutation({
    mutationFn: (battleId: string) =>
      lootFetch<BattleClaimResponse>(`/battles/${battleId}/claim`, { method: "POST" }),
    onSuccess: (_res, battleId) => invalidateAfterBattleClaim(battleId),
  });
}

/** Claims the viewer's seat and sells everything it minted (the pot, or the loser prize) straight back. */
export function useSellBackBattleWinnings() {
  return useMutation({
    mutationFn: (battleId: string) =>
      lootFetch<BattleSellBackResponse>(`/battles/${battleId}/sell-back`, { method: "POST" }),
    onSuccess: (_res, battleId) => invalidateAfterBattleClaim(battleId),
  });
}

/** Persists the shopper's auto-claim preference; future reveals then claim themselves. */
export function useUpdateAutoClaim() {
  return useMutation({
    mutationFn: (autoClaim: boolean) =>
      lootFetch<unknown>("/me/settings", {
        method: "PUT",
        body: JSON.stringify({ autoClaim }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.lootMe }),
  });
}
